/**
 * File download utilities
 * Shared utilities for handling file downloads across the application
 */

/**
 * Triggers a file download in the browser
 * @param blob - The file blob to download
 * @param fileName - The name for the downloaded file
 */
export const triggerFileDownload = (blob: Blob, fileName: string): void => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/**
 * The filename out of a `Content-Disposition` header, if it carries one.
 *
 * Handles the RFC 5987 `filename*=UTF-8''name.pdf` form as well as a plain
 * `filename="name.pdf"`, and keeps the basename only -- a server-supplied path
 * separator has no business reaching a save dialog.
 *
 * The header is only readable at all when the API sends
 * `Access-Control-Expose-Headers: Content-Disposition`.
 */
export const filenameFromContentDisposition = (
  header: string | null | undefined,
): string | undefined => {
  if (!header) return undefined;

  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(header)?.[1];
  const raw = encoded ?? plain;
  if (!raw) return undefined;

  let value = raw.trim();
  if (encoded) {
    try {
      value = decodeURIComponent(value);
    } catch {
      // A malformed escape is not worth failing a download over.
    }
  }

  const name = value.split(/[\\/]/).pop()?.trim();
  return name || undefined;
};

/**
 * Builds a URL that hints the desired filename to the server (commonly supported by S3 presigned URLs).
 * This keeps the "direct open/download" behavior (avoids CORS), while letting the browser save with a friendly name.
 *
 * For S3, the `response-content-disposition` query param overrides the response header.
 */
export const withDownloadFileName = (
  sourceUrl: string,
  downloadFileName?: string,
): string => {
  if (!sourceUrl || !downloadFileName) return sourceUrl;

  // Basic header-safety: strip newlines and quotes.
  const safeFileName = downloadFileName.replace(/[\r\n"]/g, '');
  if (!safeFileName) return sourceUrl;

  try {
    const url = new URL(sourceUrl);
    url.searchParams.set(
      'response-content-disposition',
      `attachment; filename="${safeFileName}"`,
    );
    return url.toString();
  } catch {
    // If sourceUrl isn't a valid absolute URL, don't break existing behavior.
    return sourceUrl;
  }
};

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  );
};

/**
 * Formats date for file display
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatFileDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
